/* Confetti helpers — small per-cell pop on first-green, plus a big
   viewport-scale celebration that runs concurrent with the win-fire.

   `@tsparticles/confetti` takes viewport-relative coordinates as
   percentages (0–1, *not* 0–100 like the emitters API). Origin is
   top-left. */

import { confetti } from "@tsparticles/confetti";
import { CELEBRATION_PALETTE } from "./engine";

/** Reference viewport diagonal in CSS pixels — calibrated to a typical
    desktop window. Confetti `scalar` (sprite size multiplier) is
    multiplied by `currentDiagonal / REFERENCE_DIAGONAL_PX` so individual
    pieces shrink on small windows and grow on large ones, instead of
    rendering at the same absolute size on every screen. */
const REFERENCE_DIAGONAL_PX = Math.sqrt(1280 * 1280 + 800 * 800);

function viewportScale(): number {
  const W = window.innerWidth;
  const H = window.innerHeight;
  return Math.sqrt(W * W + H * H) / REFERENCE_DIAGONAL_PX;
}

/** Tight per-cell pop on first-green: a localized twinkle at the tile,
    felt rather than watched.

    NOTE on `ticks`: @tsparticles/confetti inverts canvas-confetti's
    semantics — internally it computes opacitySpeed = ticks / 432, so
    HIGHER ticks fades FASTER (and lower ticks gives a near-immortal
    particle). Hence the very large value here. Combined with low
    gravity, particles fade within a third of the cell height of the
    emission point instead of drifting off the bottom of the viewport. */
export function popCell(cell: Element): void {
  const rect = cell.getBoundingClientRect();
  const x = ((rect.left + rect.width / 2) / window.innerWidth) * 100;
  const y = ((rect.top + rect.height / 2) / window.innerHeight) * 100;
  const scale = viewportScale();
  void confetti({
    position: { x, y },
    count: 10,
    spread: 70,
    startVelocity: 8 * scale,
    ticks: 500,
    gravity: 0.3,
    decay: 0.9,
    scalar: 0.7 * scale,
    shapes: ["circle", "square"],
    colors: [...CELEBRATION_PALETTE],
    disableForReducedMotion: true,
  });
}

/** Big two-cone celebration burst — fired once on win, concurrent with
    the masthead fire-breath. Each burst aims from a bottom-corner launch
    point at the diagonally-opposite top corner; angle and reach are
    derived from the live viewport so the trajectory looks the same on a
    portrait phone, a square window, or a wide desktop. Positive gravity
    arcs the confetti down through the playing field for a fireworks-
    fountain feel. Confetti angles: 0° = right, 90° = up, 180° = left. */
export function fireWinCelebration(): void {
  const W = window.innerWidth;
  const H = window.innerHeight;
  // Calibration: at this reach in CSS pixels, startVelocity 70 gives the
  // intended hang time + arc height on a typical desktop window.
  // Velocity scales linearly with reach so the trajectory's apex tracks
  // the opposite corner regardless of window size.
  const REFERENCE_REACH_PX = 1100;
  // Each piece of confetti scales with the viewport diagonal too — on
  // small windows the rendered sprites are smaller, so they don't look
  // gigantic relative to the playfield.
  const scale = viewportScale();

  /** Launch from (xPct, yPct) aimed at (targetXPct, targetYPct), with
     angle and velocity derived from viewport-pixel distance. */
  const burst = (
    count: number,
    xPct: number,
    yPct: number,
    targetXPct: number,
    targetYPct: number,
  ) => {
    const dx = ((targetXPct - xPct) / 100) * W;
    // Confetti y axis grows downward (CSS), so flip for the math: a
    // target above the launch yields a positive (upward) dy.
    const dy = ((yPct - targetYPct) / 100) * H;
    const reach = Math.sqrt(dx * dx + dy * dy);
    const angle = (Math.atan2(dy, dx) * 180) / Math.PI;
    const startVelocity = 70 * (reach / REFERENCE_REACH_PX);
    void confetti({
      count,
      position: { x: xPct, y: yPct },
      angle,
      spread: 80,
      startVelocity,
      ticks: 260,
      gravity: 0.9,
      decay: 0.93,
      scalar: 1.05 * scale,
      shapes: ["circle", "square"],
      colors: [...CELEBRATION_PALETTE],
      disableForReducedMotion: true,
    });
  };

  // First wave: hugs the bottom edge of the viewport (y=100) and the
  // left/right edges (x=2/98) so the spray genuinely emanates from the
  // bottom corners rather than from an inset point.
  burst(140, 2, 100, 98, 18);
  burst(140, 98, 100, 2, 18);
  // Second wave a beat later for sustain — launch points pulled inward
  // along the bottom edge, aimed at the upper inside corners for
  // tighter overlap above the playfield.
  window.setTimeout(() => {
    burst(110, 25, 100, 75, 12);
    burst(110, 75, 100, 25, 12);
  }, 600);
}
